import type {
  ArticleSchema,
  BusinessSchema,
  EventSchema,
  ItemListSchema,
  JsonLd,
  JsonLdValue,
  OrganizationSchema,
  ProductSchema,
  SeoCrumb,
  SeoImage,
  SeoSite,
  TripSchema,
  WebSiteSchema,
} from './types'

/**
 * schema.org builders.
 *
 * Structured data is a machine-readable claim about a real organisation, made to a search
 * engine that will repeat it to somebody deciding whether to drive somewhere. So these
 * builders take only what a caller can actually know, and every one of them drops a field
 * rather than emitting an empty or invented one — see `prune`.
 *
 * What the platform is deliberately **not** allowed to emit, until the facts exist:
 * `streetAddress`, `telephone`, `email`, `geo`, `openingHours`, `priceRange`,
 * `aggregateRating`, `review`, `foundingDate`. The Foundation's contact block is still the
 * template's (`fandi@gmail.com`, `Washington Ave, NY`, a map centred on a zoo in
 * Bangladesh) — that is an open blocker on `foundation-website-prd.md`, and the cost of
 * "fixing" it here would be a wrong address in a search result.
 */

/** Absolute URL for a root-relative path. Passes an already-absolute URL through. */
export const absoluteUrl = (origin: string, path: string): string =>
  /^https?:\/\//.test(path) ? path : `${origin.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`

/** Drops `undefined`, empty strings and empty arrays, recursively. */
const prune = (value: JsonLdValue | undefined): JsonLdValue | undefined => {
  if (value === undefined || value === '' || value === null) return undefined
  if (Array.isArray(value)) {
    const items = value.map(prune).filter((v): v is JsonLdValue => v !== undefined)
    return items.length > 0 ? items : undefined
  }
  if (typeof value === 'object') {
    const out: Record<string, JsonLdValue> = {}
    for (const [key, inner] of Object.entries(value as Record<string, JsonLdValue | undefined>)) {
      const kept = prune(inner)
      if (kept !== undefined) out[key] = kept
    }
    return Object.keys(out).length > 0 ? out : undefined
  }
  return value
}

/** Builds a node, dropping anything the caller could not fill in. */
const node = (type: string, fields: Record<string, JsonLdValue | undefined>): JsonLd =>
  ({ '@type': type, ...(prune(fields) as Record<string, JsonLdValue> | undefined) }) as JsonLd

/** `ImageObject` from an `SeoImage`, or the bare URL when there are no dimensions. */
const imageNode = (site: SeoSite, image: SeoImage | undefined): JsonLdValue | undefined => {
  if (!image) return undefined
  const url = absoluteUrl(site.origin, image.url)
  if (image.width === undefined || image.height === undefined) return url
  return { '@type': 'ImageObject', url, width: image.width, height: image.height }
}

/**
 * The organisation behind a site.
 *
 * `type` is `NGO` for the Foundation and `Organization` for a venture that is not itself a
 * charity. `sameAs` is how a search engine connects a site to a social profile it already
 * knows — it is the highest-value field here and the one most often left empty.
 */
export const organizationLd = (site: SeoSite, fields: OrganizationSchema): JsonLd =>
  node(fields.type ?? 'Organization', {
    '@id': `${site.origin}/#organization`,
    name: fields.name,
    url: `${site.origin}/`,
    description: fields.description,
    logo: fields.logo ? absoluteUrl(site.origin, fields.logo) : undefined,
    sameAs: fields.sameAs,
    email: fields.email,
    areaServed: fields.areaServed,
    address:
      fields.addressLocality || fields.addressCountry
        ? {
            '@type': 'PostalAddress',
            addressLocality: fields.addressLocality,
            addressCountry: fields.addressCountry,
          }
        : undefined,
    parentOrganization: fields.parentOrganization
      ? { '@type': 'Organization', name: fields.parentOrganization.name, url: fields.parentOrganization.url }
      : undefined,
  })

/**
 * The site itself.
 *
 * No `SearchAction`: a sitelinks search box is a claim that `?q=` returns results, and
 * none of the four apps has a search endpoint — the header's search modal is inert. A
 * `SearchAction` here would be a broken promise a search engine can act on.
 */
export const webSiteLd = (site: SeoSite, fields: WebSiteSchema): JsonLd =>
  node('WebSite', {
    '@id': `${site.origin}/#website`,
    url: `${site.origin}/`,
    name: site.name,
    description: fields.description,
    inLanguage: site.locale.split('_')[0],
    publisher: { '@id': `${site.origin}/#organization` },
  })

/**
 * A local business with premises.
 *
 * `type` narrows it — `AutoRepair` for the garage, `Resort` for the resort. Both are
 * schema.org subtypes of `LocalBusiness`, and the narrower type is what puts a result in
 * the right kind of panel.
 */
export const localBusinessLd = (site: SeoSite, fields: BusinessSchema): JsonLd =>
  node(fields.type, {
    '@id': `${site.origin}/#business`,
    name: fields.name,
    url: `${site.origin}/`,
    description: fields.description,
    image: imageNode(site, fields.image),
    address:
      fields.addressLocality || fields.addressCountry
        ? {
            '@type': 'PostalAddress',
            addressLocality: fields.addressLocality,
            addressCountry: fields.addressCountry,
          }
        : undefined,
    sameAs: fields.sameAs,
    email: fields.email,
    parentOrganization: fields.parentOrganization
      ? { '@type': 'Organization', name: fields.parentOrganization.name, url: fields.parentOrganization.url }
      : undefined,
    makesOffer: fields.makesOffer?.map((name) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name } })),
  })

/** The trail to a page. Built by `seoTags`; every non-home page gets one. */
export const breadcrumbLd = (site: SeoSite, crumbs: SeoCrumb[]): JsonLd =>
  node('BreadcrumbList', {
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(site.origin, crumb.path),
    })),
  })

/** A blog post. `author` is the organisation — the posts carry no personal byline. */
export const articleLd = (site: SeoSite, path: string, fields: ArticleSchema): JsonLd =>
  node('BlogPosting', {
    headline: fields.headline,
    description: fields.description,
    mainEntityOfPage: absoluteUrl(site.origin, path),
    image: imageNode(site, fields.image),
    datePublished: fields.datePublished,
    dateModified: fields.dateModified ?? fields.datePublished,
    articleSection: fields.section,
    keywords: fields.keywords?.join(', '),
    author: { '@id': `${site.origin}/#organization` },
    publisher: { '@id': `${site.origin}/#organization` },
  })

/**
 * An event.
 *
 * `eventAttendanceMode` is always the offline one — every KKF event is a place you go.
 * `location` is a bare `Place` with the name the site prints, which is often "venue to be
 * announced"; that is honest, and better than a fabricated address.
 */
export const eventLd = (site: SeoSite, path: string, fields: EventSchema): JsonLd =>
  node('Event', {
    name: fields.name,
    description: fields.description,
    url: absoluteUrl(site.origin, path),
    image: imageNode(site, fields.image),
    startDate: fields.startDate,
    endDate: fields.endDate,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: fields.locationName ? { '@type': 'Place', name: fields.locationName } : undefined,
    organizer: { '@id': `${site.origin}/#organization` },
  })

/**
 * A product.
 *
 * No `offers`. The shop prints no prices and takes no payment, and an `Offer` with an
 * invented price or a bare `availability` is the single most common way a small site earns
 * a manual action. When prices are real, add `offers` here and nowhere else.
 */
export const productLd = (site: SeoSite, path: string, fields: ProductSchema): JsonLd =>
  node('Product', {
    name: fields.name,
    description: fields.description,
    url: absoluteUrl(site.origin, path),
    image: imageNode(site, fields.image),
    brand: fields.brand ? { '@type': 'Brand', name: fields.brand } : undefined,
    category: fields.category,
    sku: fields.sku,
    manufacturer: { '@id': `${site.origin}/#organization` },
  })

/** A bookable trip. `TouristTrip` is what a tour is; `Product` would lose the itinerary. */
export const touristTripLd = (site: SeoSite, path: string, fields: TripSchema): JsonLd =>
  node('TouristTrip', {
    name: fields.name,
    description: fields.description,
    url: absoluteUrl(site.origin, path),
    image: imageNode(site, fields.image),
    tripOrigin: { '@type': 'Place', name: 'Ulaanbaatar, Mongolia' },
    itinerary: fields.itinerary?.map((name, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: { '@type': 'Place', name },
    })),
    subjectOf: fields.duration ? { '@type': 'CreativeWork', abstract: fields.duration } : undefined,
    provider: { '@id': `${site.origin}/#organization` },
  })

/** An ordered list of pages — a directory, a listing. Names and URLs only. */
export const itemListLd = (site: SeoSite, fields: ItemListSchema): JsonLd =>
  node('ItemList', {
    name: fields.name,
    numberOfItems: fields.items.length,
    itemListElement: fields.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: absoluteUrl(site.origin, item.path),
    })),
  })
